import Logo from "~/components/Logo"
import {
  articleBodyStyle,
  articleCompareImages,
  articleFaqBodyStyle,
  articleImageStyle,
  bold,
  faqGrid,
  figCaptionStyle,
  h1Style,
  h1Wrap,
  h3Style,
  headerTextStyle,
  imagePreviewBodyStyle,
  infoWrap,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Link from "~/components/Link"
import Footer from "~/components/Footer"

export const meta: MetaFunction = () => {
  return [
    {
      title: "Zen Garden Designs made with AI",
    },
    {
      name: "description",
      content:
        "See how you can use Rescape AI's garden design tool to visualise your desired Zen Garden.",
    },
    {
      property: "og:title",
      content: "Zen Garden Designs made with AI",
    },
    {
      property: "og:description",
      content:
        "See how you can use Rescape AI's garden design tool to visualise your desired Zen Garden.",
    },
    {
      property: "og:image",
      content: "https://rescapeai.com/guide/zen-1.webp",
    },
    {
      tagName: "link",
      rel: "canonical",
      href: "https://rescapeai.com/garden-design-guide/zen/zen-garden-ideas-using-ai",
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Zen Garden Design with AI",
        description:
          "See how you can use Rescape AI's garden design tool to visualise your desired Zen Garden.",
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-20",
        dateModified: "2025-10-20",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: "https://rescapeai.com/guide/zen-1.webp",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id":
            "https://rescapeai.com/garden-design-guide/zen/zen-garden-ideas-using-ai",
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/guide/zen-5.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>Garden Designs made using AI: Zen</h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <div className={page}>
        <section className={stepSectionStyle}>
          <p className={articleBodyStyle}>
            Designing a Zen garden has always been an art rooted in simplicity, balance,
            and natural harmony. Traditionally, this process required deep familiarity
            with Japanese aesthetics and careful visualization—stones, sand, moss, and
            pathways all arranged to evoke tranquility and mindful presence. Today, like
            Rescape AI offers a powerful way to explore these timeless principles with
            modern efficiency and creativity. With Rescape AI, homeowners can rapidly
            experiment with different compositions and layouts without lifting a single
            stone; just by using photographs.
          </p>
        </section>
        <section className={stepSectionStyle}>
          <div className={infoWrap}>
            <figure>
              <div className={articleCompareImages}>
                <img
                  src={"/guide/garden-7.webp"}
                  alt={"Garden before redesign"}
                  loading="lazy"
                />
                <img src={"/guide/zen-1.webp"} alt={"Zen Design by AI"} loading="lazy" />
              </div>
              <img
                className={articleImageStyle}
                src={"/guide/zen-2.webp"}
                alt={"Contemporary-design-by-ai"}
                loading="lazy"
                style={{
                  height: "320px",
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "0% 80%",
                  marginTop: "2rem",
                }}
              />
              <figcaption className={figCaptionStyle}>
                Japanese Zen Garden Style With Maple Trees and Carefully Placed Stones
              </figcaption>
            </figure>
            <p className={articleBodyStyle}>
              The AI-generated design clearly transforms the original garden into a
              refined Zen-inspired landscape. Here are the key Zen garden features it
              introduced:
            </p>
          </div>
          <ul className={unorderedList}>
            <li>
              <strong className={bold}>Curated Stone Pathways</strong> The original
              stepping stones are re-imagined as smoother, more intentional stone paths.
              In Zen design, paths symbolize a mindful journey — every stone is carefully
              placed to guide movement calmly and purposefully.
            </li>
            <li>
              <strong className={bold}>Sculpted Moss Mounds</strong> The AI replaced the
              natural ground cover with lush, rounded moss mounds. These offer softness
              and a sense of ancient tranquility, mirroring traditional Japanese temple
              gardens.
            </li>
            <li>
              <strong className={bold}>Japanese Maple Trees</strong> Bright red Japanese
              Maples were added — a striking focal point. Their delicate, layered foliage
              creates depth, seasonal beauty, and a serene contrast against the dark house
              exterior and green plant beds.
            </li>
            <li>
              <strong className={bold}>Raked Gravel Areas</strong> Gravel areas appear,
              referencing karesansui (dry rock gardens). These spaces symbolically
              represent water and bring meditative structure through their smooth
              textures.
            </li>
            <li>
              <strong className={bold}>Balance Composition & Negative Space</strong> The
              design adds flowing, open grass shapes bordered by gravel. This respects the
              Zen principle of ma — purposeful empty space that creates visual calm and
              balance.
            </li>
            <li>
              <strong className={bold}>Natural Stone Accents</strong> Large boulders and
              stone groupings remain or are emphasized, grounding the garden. In Zen
              aesthetics, stones represent mountains and permanence.
            </li>
          </ul>
        </section>
        <section className={stepSectionStyle}>
          <div className={articleCompareImages}>
            <img
              src={"/guide/garden-6.webp"}
              alt={"Garden before redesign"}
              loading="lazy"
            />
            <img src={"/guide/zen-3.webp"} alt={"Zen Design by AI"} loading="lazy" />
          </div>
          <p className={articleBodyStyle}>
            This lifeless paved corner has been beautifully transformed into a peaceful
            Zen retreat. By replacing harsh concrete with natural materials and adding
            thoughtful design elements, the space now feels calm, inviting, and full of
            quiet character. Here’s a closer look at the key features that made this
            transformation possible:
          </p>
          <ul className={unorderedList}>
            <li>
              <strong className={bold}>Concrete replaced by calming gravel</strong> — the
              hard paved surface has been swapped for pale gravel, creating a softer,
              cleaner, and more meditative base.
            </li>
            <li>
              <strong className={bold}>Low planting and greenery added</strong> — pockets
              of moss, small shrubs, and subtle grasses give life and texture without
              overwhelming the serene design.
            </li>
            <li>
              <strong className={bold}>Softened boundaries</strong> — leafy plants along
              the fence hide harsh lines, creating a more secluded, immersive space.
            </li>
          </ul>
        </section>
        <section className={stepSectionStyle}>
          <div className={articleCompareImages}>
            <img
              src={"/guide/garden-1-before-ai-design.webp"}
              alt={"Garden before redesign"}
              loading="lazy"
            />
            <img src={"/guide/zen-5.webp"} alt={"Zen Design by AI"} loading="lazy" />
          </div>
          <p className={articleBodyStyle}>
            This simple lawn has been turned into a serene, minimalist Zen garden with a
            strong sense of calm and structure. The once-plain grass area is now a
            thoughtfully designed raked-sand landscape, using shape, texture, and careful
            plant placement to create a soothing and meditative outdoor space.
          </p>
          <ul className={unorderedList}>
            <li>
              <strong className={bold}>Raked sand surface</strong> — replaces the grass
              with flowing patterns that mimic gentle water movement, adding a calm and
              artistic focal texture.
            </li>
            <li>
              <strong className={bold}>Structured cloud-form shrubs</strong> — rounded
              evergreens provide soft, organic shapes typical of Zen gardens.
            </li>
            <li>
              <strong className={bold}>Minimalist planting palette</strong> — limited
              plant variety keeps the space clean and uncluttered, letting form and
              texture shine.
            </li>
          </ul>
          <p className={articleBodyStyle}>
            These transformations show just how powerful thoughtful design can be in
            turning ordinary outdoor spaces into peaceful, inspiring retreats. If you're
            dreaming of your own Zen garden or want to{" "}
            <Link href="/garden-design-guide/contemporary/contemporary-garden-ideas-using-ai">
              explore other styles
            </Link>
            , Rescape AI makes it easy to{" "}
            <Link href="/hello/signup">visualise ideas</Link> before you lift a single
            stone. Ready to reimagine your garden and create a space you love spending
            time in? Join today and start designing your perfect sanctuary.
          </p>
        </section>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <LinkButton url="/hello/signup">Get Started here</LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
